
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Award } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Product } from '@/types/product';
import ProductCheckoutModal from '@/components/Checkout/ProductCheckoutModal';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

interface ProductBuyButtonProps {
  product: Product;
  quantity?: number;
  className?: string;
  variant?: "default" | "outline";
  size?: "sm" | "default" | "lg";
  onSuccess?: () => void;
  iconOnly?: boolean;
}

const ProductBuyButton: React.FC<ProductBuyButtonProps> = ({
  product,
  quantity = 1,
  className = "",
  variant = "default",
  size = "default",
  onSuccess,
  iconOnly = false
}) => {
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const totalPointsNeeded = product.pointsPrice * quantity;
  const userPoints = userProfile?.points || 0;
  const hasEnoughPoints = userPoints >= totalPointsNeeded;
  const hasConnectedAccount = userProfile?.connectedAccountId;

  const handleBuyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Verificar se o usuário está logado
    if (!user) {
      toast.error(t('productBuyButton.loginRequired'));
      return;
    }

    // Verificar se tem conta conectada
    if (!hasConnectedAccount) {
      toast.error(t('productBuyButton.connectAccountRequired'));
      return;
    }

    // Verificar se tem storepoints suficientes
    if (!hasEnoughPoints) {
      toast.error(t('productBuyButton.insufficientPoints'));
      return;
    }

    // Abrir modal de checkout
    setIsCheckoutOpen(true);
  };

  const handleCheckoutSuccess = () => {
    toast.success(t('productBuyButton.exchangeSuccess'), {
      description: t('productBuyButton.exchangeSuccessDesc')
        .replace('{productName}', product.name)
        .replace('{points}', totalPointsNeeded.toString())
    });
    setIsCheckoutOpen(false);

    // Chamar onSuccess se fornecido
    if (onSuccess) {
      onSuccess();
    }
  };

  // Determinar visual do botão baseado no estado
  const canExchange = user && hasConnectedAccount && hasEnoughPoints;

  return (
    <>
      <Button
        onClick={handleBuyClick}
        variant={variant}
        size={size}
        type="button"
        className={`${className} ${!canExchange ? 'opacity-70' : 'opacity-100'} active:scale-95 transition-all`}
        title={iconOnly ? t('productBuyButton.exchangeButtonSimple') : undefined}
      >
        {iconOnly ? (
          <Award size={16} />
        ) : (
          <>
            <Award className="mr-1 md:mr-2" size={14} />
            {t('productBuyButton.exchangeButtonSimple')}
          </>
        )}
      </Button>

      <ProductCheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        product={product}
        quantity={quantity}
        onSuccess={handleCheckoutSuccess}
      />
    </>
  );
};

export default ProductBuyButton;
